import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import type {
  AdminTeamRow,
  AthleteAssignment,
  Season,
  Team,
} from "@/lib/types";
import { useCarryForwardTeam } from "@/features/admin/hooks/useAdminData";
import { useBulkAssignments } from "@/features/athletes/hooks/useAthletes";
import { pickCurrentSeason, sortSeasonsNewestFirst } from "./SeasonTeamGroups";

/**
 * #908 §6 step 2 + 3 — carry a team forward into another season.
 *
 * Step 1 calls `POST /api/admin/teams/{id}/carry-forward`: a NEW team in the
 * target season (lineage pointer, copied settings, fresh stream key, no
 * staff, no roster). Step 2 lists the result's `source_roster` (the source
 * team's active assignments, all pre-selected) and rosters the picks with one
 * `POST /api/roster/assignments/bulk` call; "Skip" finishes without rostering.
 * The new team exists either way, so closing after step 1 still hands it back.
 */
export function TeamCarryForwardModal({
  team,
  seasons,
  onClose,
  onDone,
}: {
  team: AdminTeamRow;
  seasons: Season[];
  onClose: () => void;
  /** Called once the new team exists, rostered or not. */
  onDone: (created: Team) => void;
}) {
  const toast = useToast();
  const targets = useMemo(
    () =>
      sortSeasonsNewestFirst(seasons).filter(
        (season) => season.id !== team.season_id,
      ),
    [seasons, team.season_id],
  );
  const [seasonId, setSeasonId] = useState(
    () => pickCurrentSeason(targets)?.id ?? "",
  );
  // The season list loads lazily; adopt the current season once it arrives.
  useEffect(() => {
    if (seasonId) return;
    const current = pickCurrentSeason(targets);
    if (current) setSeasonId(current.id);
  }, [seasonId, targets]);
  const [name, setName] = useState(team.name);
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    team: Team;
    roster: AthleteAssignment[];
    /** "<team> created in <season>" — shown inline in step 2 and toasted
     * only once the modal closes: a toast while the roster step is open
     * covers its primary button on a phone. */
    message: string;
  } | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const carry = useCarryForwardTeam();
  const bulk = useBulkAssignments();
  const targetSeason = targets.find((season) => season.id === seasonId) ?? null;

  if (created) {
    const roster = created.roster;
    const finish = (rostered?: number) => {
      toast.success(
        rostered
          ? `${created.message} · rostered ${rostered} athlete${rostered === 1 ? "" : "s"}`
          : created.message,
      );
      onDone(created.team);
    };
    const items = roster.filter((row) => picked.has(row.player_id));
    const allPicked = items.length === roster.length;
    const togglePick = (playerId: string) =>
      setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(playerId)) next.delete(playerId);
        else next.add(playerId);
        return next;
      });
    return (
      <ConfirmDialog
        title={`Roster ${created.team.name}`}
        message={
          <div className="admin-squad-dialog-form">
            <p
              role="status"
              className="text-xs text-good"
              data-testid="admin-teams-carry-created"
            >
              {created.message}.
            </p>
            {roster.length === 0 ? (
              <p className="admin-dialog-intro">
                {team.name} has no active athletes to carry. Roster{" "}
                {created.team.name} from the Athletes directory when you are
                ready.
              </p>
            ) : (
              <>
                <p className="admin-dialog-intro">
                  {roster.length} athlete{roster.length === 1 ? "" : "s"}{" "}
                  active on {team.name}. Each pick becomes a new assignment on{" "}
                  {created.team.name} with the jersey and position copied.
                </p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-ink-muted" data-testid="admin-teams-carry-roster-count">
                    {items.length} selected
                  </span>
                  <button
                    type="button"
                    className="admin-dialog-advanced-toggle"
                    onClick={() =>
                      setPicked(
                        allPicked
                          ? new Set()
                          : new Set(roster.map((row) => row.player_id)),
                      )
                    }
                    data-testid="admin-teams-carry-roster-toggle"
                  >
                    {allPicked ? "Select none" : "Select all"}
                  </button>
                </div>
                <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
                  {roster.map((row) => {
                    const meta = [
                      row.jersey_number ? `#${row.jersey_number}` : null,
                      row.position || null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <li key={row.player_id}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={picked.has(row.player_id)}
                          className="flex w-full items-center gap-3 rounded border border-rule bg-field px-2 py-1.5 text-left text-xs text-ink transition-colors hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          onClick={() => togglePick(row.player_id)}
                          data-testid="admin-teams-carry-roster-row"
                          data-athlete-id={row.athlete_id ?? undefined}
                        >
                          <span
                            aria-hidden
                            data-checked={
                              picked.has(row.player_id) ? "true" : undefined
                            }
                            className="grid h-5 w-5 shrink-0 place-items-center rounded border-2 border-rule text-[13px] text-accent-ink data-[checked=true]:border-accent data-[checked=true]:bg-accent"
                          >
                            {picked.has(row.player_id) ? "✓" : ""}
                          </span>
                          <span className="min-w-0 truncate">
                            {row.display_name}
                          </span>
                          {meta ? (
                            <span className="ml-auto shrink-0 font-mono text-[0.65rem] text-ink-muted">
                              {meta}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {error ? (
              <p
                data-testid="admin-teams-carry-roster-error"
                className="text-destructive font-mono text-xs"
              >
                {error}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={
          roster.length === 0
            ? "Done"
            : bulk.isPending
              ? "Rostering…"
              : `Roster ${items.length} athlete${items.length === 1 ? "" : "s"}`
        }
        confirmDisabled={
          bulk.isPending || (roster.length > 0 && items.length === 0)
        }
        confirmTestId="admin-teams-carry-roster-confirm"
        cancelLabel={null}
        {...(roster.length > 0
          ? {
              altAction: {
                label: "Skip",
                onClick: () => finish(),
                "data-testid": "admin-teams-carry-skip",
              },
            }
          : {})}
        onConfirm={() => {
          if (roster.length === 0) {
            finish();
            return;
          }
          setError(null);
          bulk.mutate(
            {
              team_id: created.team.id,
              items: items.map((row) => ({
                athlete_id: String(row.athlete_id),
                jersey_number: row.jersey_number,
                position: row.position,
              })),
            },
            {
              onSuccess: (rows) => finish(rows.length),
              onError: (err) => setError(formatErr(err)),
            },
          );
        }}
        onClose={() => finish()}
        data-testid="admin-teams-carry-roster-modal"
      />
    );
  }

  return (
    <ConfirmDialog
      title={`Carry forward ${team.name}`}
      message={
        <div className="admin-squad-dialog-form">
          <p className="admin-dialog-intro">
            Creates a new team in the target season with this team&rsquo;s
            settings and a new stream key. Staff are not copied; the current
            roster is offered next.
          </p>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Target season
            </span>
            <TkticSelect
              value={seasonId}
              onChange={setSeasonId}
              options={targets.map((season) => ({
                value: season.id,
                label: season.name,
              }))}
              placeholder={
                targets.length ? "Select a season" : "No other season yet"
              }
              disabled={targets.length === 0}
              size="sm"
              ariaLabel="Target season"
              data-testid="admin-teams-carry-season"
            />
          </label>
          <div className="admin-dialog-two-column">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Team name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="admin-teams-carry-name"
                className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Team URL slug (optional)
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="auto"
                data-testid="admin-teams-carry-slug"
                className="rounded border border-rule bg-surface px-2 py-1.5 font-mono text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
          </div>
          {error ? (
            <p
              data-testid="admin-teams-carry-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={carry.isPending ? "Carrying forward…" : "Carry forward"}
      confirmDisabled={carry.isPending || targets.length === 0}
      confirmTestId="admin-teams-carry-confirm"
      onConfirm={() => {
        setError(null);
        if (!seasonId) {
          setError("Pick a target season.");
          return;
        }
        if (!name.trim()) {
          setError("Team name is required.");
          return;
        }
        carry.mutate(
          {
            teamId: team.id,
            body: {
              season_id: seasonId,
              name: name.trim(),
              slug: slug.trim() || undefined,
            },
          },
          {
            onSuccess: (result) => {
              const roster = (result.source_roster ?? []).filter(
                (row) => !!row.athlete_id,
              );
              setPicked(new Set(roster.map((row) => row.player_id)));
              setCreated({
                team: result.team,
                roster,
                message: `${result.team.name} created in ${targetSeason?.name ?? "the target season"}`,
              });
            },
            onError: (err) => setError(formatErr(err)),
          },
        );
      }}
      onClose={onClose}
      data-testid="admin-teams-carry-modal"
    />
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
